// mailMoveTargets.ts — Cluster POSTFACH, Entscheidung E02 (Zusatz)
//
// Reine Ableitungslogik für die Postfach-Umschaltung im „Verschieben nach…"-
// Untermenü: welche Postfächer als Ziel in Frage kommen, in welcher Reihenfolge
// sie stehen und wie ein gewähltes Ziel heißt.
//
// Zur Auswahl der Ziel-Postfächer (offener Punkt, bewusst so entschieden):
// E02 nennt „persönliches Postfach ODER CBB". Ein technischer Filter auf
// `mailboxType === 'shared'` wäre naheliegend, ist aber untauglich — die Spalte
// hat den Default `'user'`, und cbb@ wird ohne `mailboxType` angelegt; der
// Filter würde ausgerechnet CBB ausschließen. Deshalb: alle Postfächer mit
// Mail-Leserecht (das schließt reine Kalender-Postfächer bereits aus) außer
// Raumpostfächern. Das ist eine Ausweitung gegenüber dem Wortlaut von E02 und
// beim PO noch abzunicken; enger wird es, sobald `mailboxType` gepflegt ist.

import type { MailboxAccess } from "../../services/graphMailService";
import { canReadMailbox } from "../../utils/mailboxAccess";
import type { MailFolderEntry } from "./mailFolderTree";
import { mailboxChipLabel } from "./mailSearch";

export type MoveTarget = {
    /** Ziel-Postfach. */
    mailbox: string;
    /** Ziel-Ordner: Alias einer Standardzeile ODER Graph-Ordner-Id. */
    folderKey: string;
    folderLabel: string;
    /** true = anderes Postfach als die Quelle → serverseitiger MIME-Umweg. */
    crossMailbox: boolean;
};

/**
 * Zulässige Ziel-Postfächer in Menü-Reihenfolge: Quell-Postfach zuerst (das ist
 * der Normalfall und bleibt der billige Graph-Move), dann das eigene, dann die
 * übrigen in Listenreihenfolge.
 */
export function moveTargetMailboxes(
    mailboxes: MailboxAccess[],
    sourceMailbox: string,
): MailboxAccess[] {
    const quelle = (sourceMailbox ?? "").toLowerCase();
    const zulaessig = mailboxes.filter(
        (m) => canReadMailbox(m) && m.mailboxType !== "room",
    );
    const rang = (m: MailboxAccess): number => {
        if (m.mailboxAddress.toLowerCase() === quelle) return 0;
        if (m.isOwn) return 1;
        return 2;
    };
    return zulaessig
        .map((m, i) => ({ m, i }))
        .sort((a, b) => rang(a.m) - rang(b.m) || a.i - b.i)
        .map((x) => x.m);
}

/** Ein Ziel aus Ordnerzeile und gewähltem Postfach bauen. */
export function buildMoveTarget(
    sourceMailbox: string,
    entry: MailFolderEntry,
    targetMailbox: string,
): MoveTarget {
    return {
        mailbox: targetMailbox,
        folderKey: entry.key,
        folderLabel: entry.label,
        crossMailbox:
            (sourceMailbox ?? "").toLowerCase() !== (targetMailbox ?? "").toLowerCase(),
    };
}

/**
 * Beschriftung eines Ziels für Rückmeldungen: bei einem fremden Postfach mit
 * dessen Kurznamen davor („cbb › Kunden"), sonst nur der Ordnername.
 */
export function moveTargetLabel(t: MoveTarget): string {
    return t.crossMailbox ? `${mailboxChipLabel(t.mailbox)} › ${t.folderLabel}` : t.folderLabel;
}
